/**
 * Voice AI Components - Phase K (M3 + M4)
 * 
 * Provides UI components for Voice AI functionality:
 * - VoiceCallButton: "Call via AI" action button
 * - VoiceCallHistory: Call history list
 * - VoiceCallDetails: Call transcript and summary viewer
 * - VoiceAIStatusBadge: Shows Voice AI capability status
 * - VoiceAIPanel: Collapsible panel for Lead/Ticket detail pages
 * 
 * M4 Enhancements:
 * - Shows SIMULATED mode only (LIVE hidden for non-permitted users)
 * - Displays blocked reasons when LIVE is unavailable
 * - Demo tenant safety (LIVE permanently blocked)
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneForwarded,
  Bot,
  Loader2,
  Play,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Volume2,
  MessageSquare,
  ArrowRight,
  Mic,
  MicOff,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  Lock,
  Info,
  UserCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { voiceAPI } from '../lib/api';

// ============================================
// VOICE CALL BUTTON (M4 Enhanced)
// ============================================

export function VoiceCallButton({
  entityType,
  entityId,
  entityName,
  phoneNumber,
  disabled = false,
  variant = 'outline',
  size = 'sm',
  className = '',
  onCallComplete
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [callResult, setCallResult] = useState(null);
  const [phone, setPhone] = useState(phoneNumber || '');
  const [customMessage, setCustomMessage] = useState('');
  // M4: Voice AI permissions status
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    if (phoneNumber) {
      setPhone(phoneNumber);
    }
  }, [phoneNumber]);

  // M4: Load permission status when dialog opens
  useEffect(() => {
    if (isDialogOpen && !permissionStatus) {
      loadPermissionStatus();
    }
  }, [isDialogOpen]);

  const loadPermissionStatus = async () => {
    setLoadingStatus(true);
    try {
      const response = await voiceAPI.getStatus();
      setPermissionStatus(response.data);
    } catch (error) {
      console.error('Failed to load Voice AI status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleInitiateCall = async () => {
    if (!phone) {
      toast.error('Please enter a phone number');
      return;
    }

    setIsLoading(true);
    setCallResult(null);

    try {
      const response = await voiceAPI.initiateOutboundCall({
        entity_type: entityType,
        entity_id: entityId,
        to_number: phone,
        custom_message: customMessage || undefined,
        requested_mode: 'SIMULATED'
      });

      setCallResult(response.data);

      if (response.data.success) {
        toast.success(`Voice AI call ${response.data.status} [${response.data.mode}]`);
        if (onCallComplete) {
          onCallComplete(response.data);
        }
      } else {
        toast.error(response.data.blocked_reason || 'Call failed');
      }
    } catch (error) {
      console.error('Voice call error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
      setCallResult({
        success: false,
        mode: 'FAILED',
        status: 'failed',
        blocked_reason: error.response?.data?.detail || error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setCallResult(null);
    setCustomMessage('');
  };

  // M4: Check if voice AI is available (simulated at minimum)
  const isVoiceAvailable = !permissionStatus || permissionStatus.simulated_allowed;
  const isDemo = permissionStatus?.is_demo || false;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={() => setIsDialogOpen(true)}
        disabled={disabled || (permissionStatus && !permissionStatus.simulated_allowed)}
        data-testid="voice-call-button"
      >
        <Bot className="h-4 w-4 mr-2" />
        Call via AI
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-primary" />
              Voice AI Call
            </DialogTitle>
            <DialogDescription>
              Initiate an AI-powered call to {entityName || `this ${entityType}`}
            </DialogDescription>
          </DialogHeader>

          {loadingStatus ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !callResult ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 234 567 8900"
                  data-testid="voice-call-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Custom Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add any specific points for the AI to mention..."
                  rows={3}
                  data-testid="voice-call-message-input"
                />
              </div>

              {/* M4: Mode indicator with permission details */}
              <div className="rounded-lg bg-muted/50 p-3 text-sm">
                <div className="flex items-start gap-2">
                  {isDemo ? (
                    <ShieldAlert className="h-4 w-4 mt-0.5 text-amber-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">
                      {isDemo ? 'Demo Mode' : 'SIMULATED Mode'}
                    </p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {isDemo
                        ? 'Demo tenants use simulated calls only. No actual phone calls will be made.'
                        : 'This call will be simulated for demo purposes. No actual phone call will be made.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* M4: Show blocked reasons if LIVE is not available */}
              {permissionStatus && !permissionStatus.live_allowed && permissionStatus.live_blocked_reasons?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Lock className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-300">LIVE Mode Unavailable</p>
                      <ul className="text-amber-700 dark:text-amber-400 text-xs mt-1 space-y-0.5">
                        {permissionStatus.live_blocked_reasons.map((reason, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="mt-1.5 h-1 w-1 rounded-full bg-amber-500 flex-shrink-0" />
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 ${callResult.success ? 'bg-green-50 dark:bg-green-950' : 'bg-red-50 dark:bg-red-950'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {callResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {callResult.success ? 'Call Completed' : 'Call Failed'}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  <p><span className="text-muted-foreground">Mode:</span> {callResult.mode}</p>
                  <p><span className="text-muted-foreground">Status:</span> {callResult.status}</p>
                  {callResult.call_id && (
                    <p><span className="text-muted-foreground">Call ID:</span> {callResult.call_id}</p>
                  )}
                  {callResult.blocked_reason && (
                    <p className="text-red-600">{callResult.blocked_reason}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {!callResult ? (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleInitiateCall}
                  disabled={isLoading || !phone}
                  data-testid="voice-call-initiate-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calling...
                    </>
                  ) : (
                    <>
                      <PhoneCall className="h-4 w-4 mr-2" />
                      Start AI Call
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={handleClose}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================
// VOICE CALL HISTORY
// ============================================

export function VoiceCallHistory({
  entityType,
  entityId,
  className = ''
}) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadCallHistory();
  }, [entityType, entityId]);

  const loadCallHistory = async () => {
    setLoading(true);
    try {
      const response = await voiceAPI.getCallHistory(entityType, entityId);
      setCalls(response.data.calls || []);
    } catch (error) {
      console.error('Failed to load call history:', error);
      // Don't show error toast - may just be empty
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (call) => {
    setSelectedCall(call);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status, mode) => {
    const statusConfig = {
      completed: { variant: 'default', icon: CheckCircle, className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      initiated: { variant: 'secondary', icon: Phone, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      active: { variant: 'default', icon: PhoneCall, className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      failed: { variant: 'destructive', icon: XCircle, className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
      'no-answer': { variant: 'outline', icon: PhoneOff, className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' }
    };

    const config = statusConfig[status] || statusConfig.initiated;
    const Icon = config.icon;

    return (
      <div className="flex items-center gap-2">
        <Badge className={config.className}>
          <Icon className="h-3 w-3 mr-1" />
          {status}
        </Badge>
        {mode === 'SIMULATED' && (
          <Badge variant="outline" className="text-xs">
            <Bot className="h-3 w-3 mr-1" />
            Simulated
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-8 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className={`${className}`}>
        <Card>
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <PhoneOff className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-medium mb-1">No Voice AI Calls Yet</h3>
            <p className="text-sm text-muted-foreground">
              Use the "Call via AI" button to initiate your first AI-powered call.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {calls.map((call) => (
        <Card key={call.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  call.direction === 'outbound' 
                    ? 'bg-blue-100 dark:bg-blue-900' 
                    : 'bg-green-100 dark:bg-green-900'
                }`}>
                  {call.direction === 'outbound' ? (
                    <PhoneCall className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <Phone className="h-5 w-5 text-green-600 dark:text-green-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium">
                    {call.direction === 'outbound' ? 'Outbound AI Call' : 'Inbound AI Call'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {call.to_number} • {call.duration}s
                  </p>
                </div>
              </div>
              <div className="text-right">
                {getStatusBadge(call.status, call.mode)}
                <p className="text-xs text-muted-foreground mt-1">
                  {call.created_at && formatLocal(call.created_at, 'MMM dd, HH:mm')}
                </p>
              </div>
            </div>

            {call.transcript && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {call.transcript}
                </p>
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewDetails(call)}
                data-testid={`view-call-details-${call.id}`}
              >
                View Details
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Call Details Dialog */}
      <VoiceCallDetailsDialog
        call={selectedCall}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </div>
  );
}

// ============================================
// VOICE CALL DETAILS DIALOG (Phase N Enhanced)
// ============================================

export function VoiceCallDetailsDialog({
  call,
  open,
  onOpenChange
}) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [kbUsage, setKbUsage] = useState([]);
  const [loadingKbUsage, setLoadingKbUsage] = useState(false);
  // Phase N: Handoff state
  const [handoff, setHandoff] = useState(null);
  const [loadingHandoff, setLoadingHandoff] = useState(false);
  const [canTransfer, setCanTransfer] = useState(false);
  const [transferBlocked, setTransferBlocked] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);

  useEffect(() => {
    if (open && call?.id) {
      loadSummary();
      loadKbUsage();
      loadHandoffDetails();
      checkCanTransfer();
    }
  }, [open, call?.id]);

  const loadSummary = async () => {
    if (!call?.id) return;
    setLoadingSummary(true);
    try {
      const response = await voiceAPI.getCallSummary(call.id);
      if (response.data.found) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to load call summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadKbUsage = async () => {
    if (!call?.id) return;
    setLoadingKbUsage(true);
    try {
      const response = await voiceAPI.getKBUsage(call.id);
      if (response.data.found) {
        setKbUsage(response.data.kb_usage || []);
      }
    } catch (error) {
      console.error('Failed to load KB usage:', error);
    } finally {
      setLoadingKbUsage(false);
    }
  };

  // Phase N: Load handoff details
  const loadHandoffDetails = async () => {
    if (!call?.id) return;
    setLoadingHandoff(true);
    try {
      const response = await voiceAPI.getHandoffDetails(call.id);
      if (response.data.has_handoff) {
        setHandoff(response.data.handoff);
      }
    } catch (error) {
      console.error('Failed to load handoff details:', error);
    } finally {
      setLoadingHandoff(false);
    }
  };

  // Phase N: Check if transfer is allowed
  const checkCanTransfer = async () => {
    if (!call?.id) return;
    try {
      const response = await voiceAPI.canTransfer(call.id);
      setCanTransfer(response.data.can_transfer);
      setTransferBlocked(response.data.blocked_reason);
    } catch (error) {
      console.error('Failed to check transfer status:', error);
      setCanTransfer(false);
    }
  };

  // Phase N: Handle transfer to human
  const handleTransferToHuman = async () => {
    if (!call?.id) return;
    
    setTransferLoading(true);
    try {
      const response = await voiceAPI.transferToHuman(call.id, 'manual_transfer');
      
      if (response.data.success) {
        toast.success('Call transferred to human agent');
        setHandoff(response.data);
        loadHandoffDetails();
        setShowTransferConfirm(false);
      } else {
        toast.error(response.data.blocked_reason || 'Transfer failed');
      }
    } catch (error) {
      console.error('Transfer failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer call');
    } finally {
      setTransferLoading(false);
    }
  };

  if (!call) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Voice Call Details
          </DialogTitle>
          <DialogDescription>
            {call.direction === 'outbound' ? 'Outbound' : 'Inbound'} call to {call.to_number}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          <div className="space-y-4 pb-4">
            {/* Call Info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Call Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Direction</span>
                  <span className="capitalize">{call.direction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={call.status === 'completed' ? 'default' : 'secondary'}>
                    {call.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mode</span>
                  <Badge variant="outline">{call.mode}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span>{call.duration}s</span>
                </div>
                {/* Phase M: Language info */}
                {call.language_code && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Language</span>
                    <Badge variant="outline" className="capitalize">
                      {call.language_code === 'en' ? '🇺🇸 English' : 
                       call.language_code === 'hi' ? '🇮🇳 Hindi' : 
                       call.language_code === 'es' ? '🇪🇸 Spanish' : call.language_code}
                    </Badge>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span>{call.created_at && formatLocal(call.created_at)}</span>
                </div>
                {/* Phase N: Transferred indicator */}
                {call.transferred && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transfer Status</span>
                    <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                      <PhoneForwarded className="h-3 w-3 mr-1" />
                      Transferred
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Phase N: Transfer to Human Button */}
            {!call.transferred && !handoff && (
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserCircle className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">Transfer to Human</p>
                        <p className="text-xs text-muted-foreground">
                          Hand off this call to a human agent with full context
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTransferConfirm(true)}
                      disabled={!canTransfer || transferLoading}
                      data-testid="transfer-to-human-btn"
                    >
                      {transferLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <PhoneForwarded className="h-4 w-4 mr-1" />
                          Transfer
                        </>
                      )}
                    </Button>
                  </div>
                  {transferBlocked && !canTransfer && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {transferBlocked}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Phase N: Handoff Details (if transferred) */}
            {loadingHandoff ? (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading handoff details...</span>
                  </div>
                </CardContent>
              </Card>
            ) : handoff ? (
              <Card className="border-orange-200 dark:border-orange-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <PhoneForwarded className="h-4 w-4 text-orange-600" />
                    Handoff to Human Agent
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Call was transferred on {handoff.created_at && formatLocal(handoff.created_at, 'MMM dd, HH:mm')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trigger Reason</span>
                    <Badge variant="outline" className="capitalize">
                      {handoff.trigger_reason?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transferred To</span>
                    <span>{handoff.transferred_to}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge 
                      variant={handoff.status === 'completed' ? 'default' : 'secondary'}
                      className={handoff.status === 'completed' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : handoff.status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        : ''
                      }
                    >
                      {handoff.status}
                    </Badge>
                  </div>
                  {handoff.summary_snapshot && (
                    <div className="pt-2 border-t">
                      <Label className="text-xs text-muted-foreground">AI Summary (Shared with Agent)</Label>
                      <p className="text-sm mt-1 bg-muted/50 rounded p-2">
                        {handoff.summary_snapshot}
                      </p>
                    </div>
                  )}
                  {handoff.context_package?.kb_sources_referenced?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">KB Sources Referenced</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {handoff.context_package.kb_sources_referenced.map((src, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {src.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null}

            {/* Transcript */}
            {call.transcript && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Transcript
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-3 text-sm whitespace-pre-wrap">
                    {call.transcript}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Summary */}
            {loadingSummary ? (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading AI summary...</span>
                  </div>
                </CardContent>
              </Card>
            ) : summary ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Bot className="h-4 w-4" />
                    AI Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Summary</Label>
                    <p className="text-sm mt-1">{summary.ai_summary}</p>
                  </div>

                  {summary.key_intents && summary.key_intents.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Key Intents</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {summary.key_intents.map((intent, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {intent.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {summary.next_actions && summary.next_actions.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Next Actions</Label>
                      <ul className="text-sm mt-1 space-y-1">
                        {summary.next_actions.map((action, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            {action.replace(/_/g, ' ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {summary.confidence_score && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Confidence Score</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${summary.confidence_score * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">
                          {Math.round(summary.confidence_score * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null}

            {/* Knowledge Base Usage (Phase L - AI Explainability) */}
            {loadingKbUsage ? (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading KB usage...</span>
                  </div>
                </CardContent>
              </Card>
            ) : kbUsage.length > 0 ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    AI Knowledge Sources Used
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Sources that grounded the AI responses during this call
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {kbUsage.map((usage, idx) => (
                    <div key={idx} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Query</span>
                        <Badge variant={usage.fallback_used ? "secondary" : "default"} className="text-xs">
                          {usage.fallback_used ? "Fallback" : "KB Grounded"}
                        </Badge>
                      </div>
                      <p className="text-sm italic truncate">"{usage.query_text}"</p>
                      
                      {usage.source_details && usage.source_details.length > 0 && (
                        <div>
                          <span className="text-xs text-muted-foreground">Sources:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {usage.source_details.map((src, i) => (
                              <Badge key={i} variant="outline" className="text-xs capitalize">
                                {src.type}: {src.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {usage.confidence_score > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Confidence:</span>
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${usage.confidence_score * 100}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium">
                            {Math.round(usage.confidence_score * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : null}

            {/* Recording (placeholder for LIVE mode) */}
            {call.recording_url && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Mic className="h-4 w-4" />
                    Recording
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <audio controls className="w-full">
                    <source src={call.recording_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Phase N: Transfer Confirmation Dialog */}
      <Dialog open={showTransferConfirm} onOpenChange={setShowTransferConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneForwarded className="h-5 w-5 text-orange-600" />
              Transfer to Human Agent
            </DialogTitle>
            <DialogDescription>
              This will transfer the call to a human agent. The agent will receive 
              the full transcript and AI summary for context.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Call ID</span>
                <span className="font-mono text-xs">{call?.id?.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Entity</span>
                <span>{call?.entity_name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span>{call?.duration}s</span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Once transferred, the AI will disengage completely and the human agent 
              will take over the call.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleTransferToHuman}
              disabled={transferLoading}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-transfer-btn"
            >
              {transferLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Transferring...
                </>
              ) : (
                <>
                  <PhoneForwarded className="h-4 w-4 mr-2" />
                  Transfer Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// ============================================
// VOICE AI STATUS BADGE (M4 Enhanced)
// ============================================

export function VoiceAIStatusBadge({ className = '' }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await voiceAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load Voice AI status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Skeleton className="h-6 w-24" />;
  }

  if (!status) return null;

  // M4: Show demo badge for demo tenants
  if (status.is_demo) {
    return (
      <Badge
        variant="secondary"
        className={`${className} bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300`}
      >
        <ShieldAlert className="h-3 w-3 mr-1" />
        Demo Mode
      </Badge>
    );
  }

  return (
    <Badge
      variant={status.can_make_calls ? 'default' : 'secondary'}
      className={className}
    >
      <Bot className="h-3 w-3 mr-1" />
      Voice AI: {status.effective_mode}
    </Badge>
  );
}

// ============================================
// VOICE AI COLLAPSIBLE PANEL
// ============================================

export function VoiceAIPanel({
  entityType,
  entityId,
  entityName,
  phoneNumber,
  defaultOpen = false,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCallHistory();
    }
  }, [isOpen, entityType, entityId]);

  const loadCallHistory = async () => {
    setLoading(true);
    try {
      const response = await voiceAPI.getCallHistory(entityType, entityId);
      setCalls(response.data.calls || []);
    } catch (error) {
      console.error('Failed to load call history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCallComplete = () => {
    loadCallHistory();
  };

  const handleViewDetails = (call) => {
    setSelectedCall(call);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status, mode) => {
    const statusConfig = {
      completed: { className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      initiated: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      active: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      failed: { className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
      'no-answer': { className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' }
    };

    const config = statusConfig[status] || statusConfig.initiated;

    return (
      <div className="flex items-center gap-1">
        <Badge className={`text-xs ${config.className}`}>
          {status}
        </Badge>
        {mode === 'SIMULATED' && (
          <Badge variant="outline" className="text-xs">
            <Bot className="h-3 w-3 mr-1" />
            Sim
          </Badge>
        )}
      </div>
    );
  };

  return (
    <Card className={className} data-testid="voice-ai-panel">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <Bot className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle className="text-sm font-medium">Voice AI Calls</CardTitle>
                  {calls.length > 0 && (
                    <p className="text-xs text-muted-foreground">{calls.length} call{calls.length !== 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <VoiceCallButton
                  entityType={entityType}
                  entityId={entityId}
                  entityName={entityName}
                  phoneNumber={phoneNumber}
                  onCallComplete={handleCallComplete}
                  size="sm"
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-3 w-1/3" />
                      <Skeleton className="h-2 w-1/2" />
                    </div>
                    <Skeleton className="h-6 w-16" />
                  </div>
                ))}
              </div>
            ) : calls.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <PhoneOff className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No Voice AI calls yet</p>
                <p className="text-xs mt-1">Click "Call via AI" to start</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[300px]">
                <div className="space-y-2">
                  {calls.map((call) => (
                    <div
                      key={call.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => handleViewDetails(call)}
                      data-testid={`voice-call-item-${call.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          call.direction === 'outbound' 
                            ? 'bg-blue-100 dark:bg-blue-900' 
                            : 'bg-green-100 dark:bg-green-900'
                        }`}>
                          {call.direction === 'outbound' ? (
                            <PhoneCall className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <Phone className="h-4 w-4 text-green-600 dark:text-green-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {call.direction === 'outbound' ? 'Outbound' : 'Inbound'} AI Call
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {call.to_number} • {call.duration}s • {call.created_at && formatLocal(call.created_at, 'MMM dd, HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(call.status, call.mode)}
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            {/* Transcript Preview for most recent call */}
            {calls.length > 0 && calls[0].transcript && (
              <div className="mt-3 pt-3 border-t">
                <Label className="text-xs text-muted-foreground">Latest Call Transcript</Label>
                <p className="text-sm mt-1 text-muted-foreground line-clamp-3 bg-muted/30 p-2 rounded">
                  {calls[0].transcript}
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      {/* Call Details Dialog */}
      <VoiceCallDetailsDialog
        call={selectedCall}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </Card>
  );
}

export default {
  VoiceCallButton,
  VoiceCallHistory,
  VoiceCallDetailsDialog,
  VoiceAIStatusBadge,
  VoiceAIPanel
};
